import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchDictBatch } from '@/api/dict';
import { getPublicTools, type ToolDto } from '@/api/tool';
import { getPublicCms } from '@/api/cms';

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

interface ToolsSectionContent {
  tag: string;
  title: string;
  subtitle: string;
  btnText: string;
}

const EMPTY_TOOLS_CONTENT: ToolsSectionContent = {
  tag: '',
  title: '',
  subtitle: '',
  btnText: '',
};

function normalizeToolsContent(raw: unknown): ToolsSectionContent {
  if (!raw || typeof raw !== 'object') return EMPTY_TOOLS_CONTENT;
  const parsed = raw as Record<string, unknown>;
  return {
    tag: readText(parsed.tag),
    title: readText(parsed.title),
    subtitle: readText(parsed.subtitle),
    btnText: readText(parsed.btnText),
  };
}

function hasAnyAttachment(tool: ToolDto): boolean {
  if (!Array.isArray(tool.attachments)) return false;
  return tool.attachments.some((item) => readText(item?.url).length > 0);
}

export default function ToolsSection() {
  const [tools, setTools] = useState<ToolDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [content, setContent] = useState<ToolsSectionContent>(EMPTY_TOOLS_CONTENT);
  const [contentError, setContentError] = useState<string | null>(null);
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    fetchDictBatch(['tool_category'])
      .then((dicts) => {
        if (dicts['tool_category']) {
          setDictCategories(dicts['tool_category'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load tool categories in homepage section:', err));
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    if (!cat) return '无';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const refreshContent = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setContent(normalizeToolsContent(cms?.tools));
      setContentError(null);
    } catch (err) {
      setContent(EMPTY_TOOLS_CONTENT);
      setContentError(err instanceof Error ? err.message : '首页工具区内容加载失败');
    }
  }, []);

  const refreshTools = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicTools();
      setTools(Array.isArray(res?.list) ? res.list : []);
    } catch (err) {
      setTools([]);
      setLoadError(err instanceof Error ? err.message : '获取工具失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshContent();
    const handleUpdate = () => void refreshContent();
    window.addEventListener('wanxiang_page_content_updated', handleUpdate);
    return () => window.removeEventListener('wanxiang_page_content_updated', handleUpdate);
  }, [refreshContent]);

  useEffect(() => {
    void refreshTools();
    const handleCustom = () => void refreshTools();
    window.addEventListener('wanxiang_tools_updated', handleCustom);
    return () => {
      window.removeEventListener('wanxiang_tools_updated', handleCustom);
    };
  }, [refreshTools]);

  const featured = useMemo(() => {
    return [...tools]
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return Number(a.sortOrder ?? 999) - Number(b.sortOrder ?? 999);
      })
      .slice(0, 4);
  }, [tools]);

  return (
    <section id="tools" className="py-24 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f97316]/10 text-[#f97316] text-sm font-medium mb-4">
            <i className="ri-tools-line"></i>
            {content.tag || '无'}
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{content.title || '无'}</h2>
          <p className="text-gray-500 max-w-xl mx-auto">{content.subtitle || '无'}</p>
          {contentError && (
            <p className="text-xs text-red-500 mt-3">区块内容加载失败：{contentError}</p>
          )}
        </div>

        {isLoading ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-500">
            正在加载工具...
          </div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-sm text-red-500">
            工具数据加载失败：{loadError}
          </div>
        ) : featured.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-400">
            暂无可展示工具
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((tool) => {
              const name = readText(tool.name) || '无';
              const description = readText(tool.description) || '无';
              const category = readText(tool.category) || '无';
              const version = readText(tool.version);
              const downloadable = hasAnyAttachment(tool);
              return (
                <div
                  key={String(tool.id ?? name)}
                  className="bg-white border border-gray-100 rounded-2xl p-5 hover:border-[#f97316]/30 transition-all duration-300 group relative"
                >
                  {tool.pinned && (
                    <span className="absolute top-3 right-3 bg-gradient-to-r from-[#f97316] to-[#ef4444] text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                      <i className="ri-fire-fill text-xs"></i>
                      热门
                    </span>
                  )}
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#f97316]/10 to-[#f97316]/5 rounded-xl mb-4 group-hover:from-[#f97316]/20 transition-all">
                    <i className="ri-tools-fill text-[#f97316] text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{name}</h3>
                  <p className="text-gray-500 text-xs mb-3 line-clamp-2">{description}</p>

                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-0.5 bg-gray-50 text-gray-500 text-xs rounded-md">{getCategoryLabel(category)}</span>
                    <span className="text-gray-400 text-xs">{version ? `v${version}` : '无版本'}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${downloadable ? 'text-[#10b981]' : 'text-gray-400'}`}>
                      {downloadable ? '含下载资源' : '暂无下载资源'}
                    </span>
                    <Link
                      to={`/tools/${tool.id}`}
                      className="px-4 py-1.5 bg-[#f97316]/10 text-[#f97316] text-xs font-medium rounded-lg hover:bg-[#f97316] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                    >
                      查看详情
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#f97316] text-[#f97316] font-semibold rounded-xl hover:bg-[#f97316] hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            {content.btnText || '无'}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
